package com.tixforgigs.common.core.util

import com.tixforgigs.apax.models.AuthToken
import com.tixforgigs.apax.models.CartResponse
import com.tixforgigs.apax.models.Order
import com.tixforgigs.apax.models.OrderResponse
import com.tixforgigs.common.core.extensions.orElse
import com.tixforgigs.common.core.extensions.tryCatchSentry
import com.tixforgigs.common.core.util.AuthUtil.currentBearerToken
import com.tixforgigs.common.core.util.AuthUtil.isUserLoggedIn
import com.tixforgigs.common.core.util.IdHelper.templateId
import com.tixforgigs.common.core.util.StorageUtil.LocalStorageState.mCurrentOrderMetadataState
import com.tixforgigs.common.core.util.StorageUtil.LocalStorageState.mCurrentOrderState
import com.tixforgigs.common.core.util.StorageUtil.LocalStorageState.mShoppingCartState
import com.tixforgigs.common.core.util.StorageUtil.LocalStorageState.mUserLocaleState
import io.github.oshai.kotlinlogging.KotlinLogging
import io.github.oshai.kotlinlogging.logger
import kotlinx.browser.window
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.serialization.KSerializer
import kotlinx.serialization.json.Json


object StorageUtil {

  val log by KotlinLogging.logger()

  sealed class StorageKey(val keyValue: String) {
    object ShoppingCart : StorageKey("shoppingCart-${templateId}")
    object SeatsHoldToken : StorageKey("seatsio-${templateId}")
    object CurrentOrderMetadata : StorageKey("currentOrderMetadata-${templateId}")
    object TemplateEvents : StorageKey("templateEvents-${templateId}")

    /**** TODO making template specific if auth-flow <-> templateId as additional oauth header is fixed **/
    object UniqueId : StorageKey("uniqueId")
    object LoginStartLocation : StorageKey("loginStartLocation")
    object AuthToken : StorageKey("authToken")
    object AuthGrantType : StorageKey("authGrantType")

    /**** **/
    object AvailableShippingMethods : StorageKey("availableShippingMethods-${templateId}")
    object SelectedShippingMethod : StorageKey("selectedShippingMethod-${templateId}")
    object UserLocale : StorageKey("userLocale")
    object PaymentCountryCode : StorageKey("paymentCountry-${templateId}")
    object Orders : StorageKey("orders-${templateId}")
    object CurrentOrder : StorageKey("currentOrder-${templateId}")
  }

  object LocalStorageState {

    val mShoppingCartState = MutableStateFlow<CartResponse?>(getLocalStorageItem(StorageKey.ShoppingCart))

    /** is currently not used, will replace mCurrentOrderState in future if orderItems are attached to OrderResponse */
    val mCurrentOrderMetadataState =
      MutableStateFlow<OrderResponse?>(getLocalStorageItem(StorageKey.CurrentOrderMetadata))

    /** can be deleted if orderItems attached to OrderResponse */
    val mCurrentOrderState = MutableStateFlow<Order?>(getLocalStorageItem(StorageKey.CurrentOrder))
    val mUserLocaleState = MutableStateFlow<String?>(getLocalStorageItem(StorageKey.UserLocale))
  }

  inline fun <reified T> getLocalStorageItem(key: StorageKey): T? {
    return window.localStorage.getItem(key.keyValue)?.let { json ->
      runCatching { Json.decodeFromString<T>(json) }.getOrNull()
    }.orElse { null }
  }

  inline fun <reified T> getSessionStorageItem(key: StorageKey): T? {
    return window.sessionStorage.getItem(key.keyValue)?.let { json ->
      runCatching { Json.decodeFromString<T>(json) }.getOrNull()
    }.orElse { null }
  }

  inline fun <reified T> setLocalStorageItem(
    key: StorageKey,
    item: T,
    serializer: KSerializer<T>,
  ) {
    tryCatchSentry {
      Json.encodeToString(serializer, item)
    }.getOrNull()?.let { json ->
      window.localStorage.setItem(key.keyValue, json)
      when (key) {
        StorageKey.ShoppingCart -> {
          mShoppingCartState.update { item as CartResponse? }
        }

        StorageKey.CurrentOrderMetadata -> {
          mCurrentOrderMetadataState.update { item as OrderResponse? }
        }

        StorageKey.CurrentOrder -> {
          mCurrentOrderState.update { item as Order? }
        }

        StorageKey.UserLocale -> {
          mUserLocaleState.update { item as String? }
        }

        StorageKey.AuthToken -> {
          currentBearerToken.update { item.unsafeCast<AuthToken>() }
          isUserLoggedIn.update { true }
        }

        else -> Unit
      }
    }
  }

  fun deleteLocalStorageItem(key: StorageKey) {
    window.localStorage.removeItem(key.keyValue)
    when (key) {
      StorageKey.ShoppingCart -> {
        mShoppingCartState.update { null }
      }

      StorageKey.CurrentOrderMetadata -> {
        mCurrentOrderMetadataState.update { null }
      }

      StorageKey.CurrentOrder -> {
        mCurrentOrderState.update { null }
      }

      StorageKey.UserLocale -> {
        mUserLocaleState.update { null }
      }

      StorageKey.AuthToken -> {
        currentBearerToken.update { null }
        isUserLoggedIn.update { false }
        deleteLocalStorageItem(StorageKey.Orders)
      }

      else -> Unit
    }
  }
}
